import { createFileRoute } from '@tanstack/react-router';
import { useState, useRef, useEffect } from 'react';
import { useTimezoneStore } from '@/stores/timezoneStore';
import { formatInUserTimezone, getTimezoneAbbreviation } from '@/utils/timezone';
import { TzDateTimeInput } from '../../../components/datetime/TzDateTimeInput';
import { fetchSSE } from '@/utils/fetchSSE';
import { Button } from '../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { useDeleteAllPickemsMutation } from '../../../api/dev-data/deleteAllPickems';
import { useCreateDemoPickemMutation } from '../../../api/dev-data/createDemoPickem';
import { useFlushCacheMutation } from '../../../api/dev-data/flushCache';
import { useBustDailyRevealCacheMutation } from '../../../api/dev-data/bustDailyRevealCache';
import { useRegenerateAggregateLeaderboards } from '../../../api/leaderboard/regenerateAggregateLeaderboards';
import { buildResendEventStreamUrl } from '../../../api/dev-data/resendEventStream';
import {
  useEventStreamPreviewQuery,
  type EventStreamPreviewParams,
} from '../../../api/dev-data/eventStreamPreview';
import { env } from '../../../config/env';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Shield,
  Activity,
  Database,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  useRedisHealthQuery,
  useRedisKeysQuery,
  useRedisValueQuery,
} from '../../../api/dev-data/redis';
import type { RedisKeyInfo } from '../../../api/dev-data/redis';
import { Badge } from '../../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';

export const Route = createFileRoute('/dashboard/dev-tools/')({
  component: DevToolsPage,
});

interface ProgressMessage {
  message: string;
  timestamp: Date;
  type?: 'info' | 'success' | 'error';
  success?: boolean;
  duration?: string;
  stats?: {
    totalUsers: number;
    pickemsCreated: number;
    questionsPerPickem: number;
    avgUsersPerPickem: number;
  };
  error?: string;
  details?: string;
}

function DevToolsPage() {
  const { timezone } = useTimezoneStore();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressMessages, setProgressMessages] = useState<ProgressMessage[]>([]);
  const [generationStatus, setGenerationStatus] = useState<
    'idle' | 'generating' | 'success' | 'error'
  >('idle');
  const scrollRef = useRef<HTMLDivElement>(null);
  const generateAbortRef = useRef<AbortController | null>(null);
  const [redisPattern, setRedisPattern] = useState('*');
  const [redisSearchEnabled, setRedisSearchEnabled] = useState(false);
  const [selectedRedisKey, setSelectedRedisKey] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const redisHealthQuery = useRedisHealthQuery();
  const redisKeysQuery = useRedisKeysQuery(redisPattern, redisSearchEnabled);
  const redisValueQuery = useRedisValueQuery(selectedRedisKey);

  const deleteMutation = useDeleteAllPickemsMutation();
  const createDemoPickemMutation = useCreateDemoPickemMutation();
  const flushCacheMutation = useFlushCacheMutation();
  const bustDailyRevealCacheMutation = useBustDailyRevealCacheMutation();
  const regenerateMutation = useRegenerateAggregateLeaderboards();
  const isProduction = env.NODE_ENV === 'production';

  // Resend event stream state
  const [resendPickemId, setResendPickemId] = useState('');
  const [resendBeforeUtc, setResendBeforeUtc] = useState('');
  const [resendBatchSize, setResendBatchSize] = useState('50');
  const [resendBatchDelayMs, setResendBatchDelayMs] = useState('200');
  const [isResending, setIsResending] = useState(false);
  const [resendMessages, setResendMessages] = useState<ProgressMessage[]>([]);
  const [resendStatus, setResendStatus] = useState<'idle' | 'running' | 'success' | 'error'>(
    'idle'
  );
  const resendScrollRef = useRef<HTMLDivElement>(null);
  const resendAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (resendScrollRef.current) {
      resendScrollRef.current.scrollTop = resendScrollRef.current.scrollHeight;
    }
  }, [resendMessages]);

  useEffect(() => {
    return () => {
      resendAbortRef.current?.abort();
    };
  }, []);

  // Preview query state
  const [previewParams, setPreviewParams] = useState<EventStreamPreviewParams | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const previewQuery = useEventStreamPreviewQuery(
    previewParams ? { ...previewParams, page: previewPage } : null
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [progressMessages]);

  useEffect(() => {
    return () => {
      generateAbortRef.current?.abort();
    };
  }, []);

  if (!env.ENABLE_DEV_TOOLS) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>Dev Tools are not enabled for this environment.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show warning gate if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-500 dark:border-red-600">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Shield className="h-16 w-16 text-red-600 dark:text-red-500" />
            </div>
            <CardTitle className="text-2xl text-red-700 dark:text-red-400">
              ⚠️ Here Be Dragons!
            </CardTitle>
            <CardDescription className="text-base mt-4 text-gray-700 dark:text-gray-300 font-medium">
              You probably don't need to use these tools. They have the potential to remove all
              data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-amber-300 bg-amber-100 dark:border-amber-700 dark:bg-amber-900/40">
              <AlertTriangle className="h-4 w-4 text-amber-700 dark:text-amber-400" />
              <AlertTitle className="text-amber-900 dark:text-amber-200 font-semibold">
                Warning
              </AlertTitle>
              <AlertDescription className="text-amber-800 dark:text-amber-300 font-medium">
                The Dev Tools can permanently delete data and should only be used by developers who
                understand the consequences.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label
                htmlFor="password"
                className="text-sm font-semibold text-gray-700 dark:text-gray-200"
              >
                Type the word{' '}
                <span className="font-mono text-red-600 dark:text-red-400 font-bold">"danger"</span>{' '}
                to proceed:
              </Label>
              <Input
                id="password"
                type="text"
                value={passwordInput}
                onChange={e => setPasswordInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && passwordInput.toLowerCase() === 'danger') {
                    setIsAuthenticated(true);
                  }
                }}
                placeholder="Enter the magic word..."
                className="font-mono"
              />
              <Button
                onClick={() => {
                  if (passwordInput.toLowerCase() === 'danger') {
                    setIsAuthenticated(true);
                  }
                }}
                disabled={passwordInput.toLowerCase() !== 'danger'}
                className="w-full"
                variant={passwordInput.toLowerCase() === 'danger' ? 'destructive' : 'default'}
              >
                {passwordInput.toLowerCase() === 'danger' ? 'Enter Dev Tools' : 'Access Denied'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleGenerate = () => {
    setIsGenerating(true);
    setGenerationStatus('generating');
    setProgressMessages([]);

    const abort = new AbortController();
    generateAbortRef.current = abort;

    fetchSSE(
      `${env.API_BASE_URL}/api/admin/dev-data/generate-pickems`,
      data => {
        const d = data as Record<string, unknown>;
        const newMessage: ProgressMessage = {
          message: d.message as string,
          timestamp: new Date(),
          type: d.error ? 'error' : d.success ? 'success' : 'info',
          success: d.success as boolean | undefined,
          duration: d.duration as string | undefined,
          stats: d.stats as ProgressMessage['stats'],
          error: d.error as string | undefined,
          details: d.details as string | undefined,
        };

        setProgressMessages(prev => [...prev, newMessage]);

        if (d.error) {
          setGenerationStatus('error');
          setIsGenerating(false);
          abort.abort();
        } else if (d.success) {
          setGenerationStatus('success');
          setIsGenerating(false);
          abort.abort();
        }
      },
      abort.signal
    ).catch(err => {
      if (abort.signal.aborted) return;
      console.error('SSE Error:', err);
      setProgressMessages(prev => [
        ...prev,
        {
          message: err instanceof Error ? err.message : 'Connection error occurred',
          timestamp: new Date(),
          type: 'error',
          error: 'Failed to connect to server',
        },
      ]);
      setGenerationStatus('error');
      setIsGenerating(false);
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const handleFlushCache = () => {
    flushCacheMutation.mutate();
  };

  const handleRegenerateLeaderboards = () => {
    regenerateMutation.mutate();
  };

  const handleClearLog = () => {
    setProgressMessages([]);
    setGenerationStatus('idle');
  };

  const handlePreviewEntries = () => {
    if (!resendPickemId.trim()) return;
    setPreviewPage(1);
    setPreviewParams({
      pickemId: resendPickemId.trim(),
      beforeUtc: resendBeforeUtc || undefined,
      page: 1,
    });
  };

  const handleResendEventStream = () => {
    if (!resendPickemId.trim()) return;
    setIsResending(true);
    setResendStatus('running');
    setResendMessages([]);

    const beforeUtcIso = resendBeforeUtc || undefined;

    const url = buildResendEventStreamUrl({
      pickemId: resendPickemId.trim(),
      beforeUtc: beforeUtcIso,
      batchSize: parseInt(resendBatchSize, 10) || 50,
      batchDelayMs: parseInt(resendBatchDelayMs, 10),
    });

    const abort = new AbortController();
    resendAbortRef.current = abort;

    fetchSSE(
      url,
      data => {
        const d = data as Record<string, unknown>;
        const msg: ProgressMessage = {
          message: d.message as string,
          timestamp: new Date(),
          type: d.error ? 'error' : d.success ? 'success' : 'info',
          success: d.success as boolean | undefined,
          error: d.error as string | undefined,
          details: d.details as string | undefined,
        };
        setResendMessages(prev => [...prev, msg]);
        if (d.error) {
          setResendStatus('error');
          setIsResending(false);
          abort.abort();
        } else if (d.success) {
          setResendStatus('success');
          setIsResending(false);
        }
      },
      abort.signal
    ).catch(err => {
      if (abort.signal.aborted) return;
      setResendMessages(prev => [
        ...prev,
        {
          message: err instanceof Error ? err.message : 'Connection error',
          timestamp: new Date(),
          type: 'error',
          error: 'SSE connection failed',
        },
      ]);
      setResendStatus('error');
      setIsResending(false);
    });
  };

  const formatTimestamp = (date: Date) => {
    return formatInUserTimezone(date, timezone, 'h:mm:ss a');
  };

  const getMessageColor = (type?: string) => {
    switch (type) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-slate-600 dark:text-slate-300';
    }
  };

  const formatRedisValue = (value: unknown): string => {
    if (value === null || value === undefined) return 'null';
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return String(value);
    }
  };

  const handleRedisKeyClick = (key: string) => {
    if (expandedKey === key) {
      setExpandedKey(null);
      setSelectedRedisKey(null);
    } else {
      setExpandedKey(key);
      setSelectedRedisKey(key);
    }
  };

  const handleRedisSearch = () => {
    setRedisSearchEnabled(true);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Breadcrumbs overrides={{ 'dev-tools': 'Dev Tools' }} />
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Dev Tools</h1>
        <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
          <AlertDescription className="text-yellow-800 dark:text-yellow-200">
            {isProduction
              ? '⚠️ Production Mode - Only safe operations (cache management, leaderboard regeneration) are available'
              : '⚠️ Development Only - All dev tools are available'}
          </AlertDescription>
        </Alert>
      </div>

      <div className="space-y-6">
        {/* Generate Dummy Data Card - Hide in production */}
        {!isProduction && (
          <Card>
            <CardHeader>
              <CardTitle>Dummy Data Generator</CardTitle>
              <CardDescription>
                Generate test pickems with questions and user selections for development and testing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>This will create:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>120 Test users</li>
                  <li>35 Total pickems:</li>
                  <ul className="list-circle list-inside ml-6 space-y-0.5">
                    <li>4 Active pickems (future dates: today + 1, 2, 3, 4 days at 7pm)</li>
                    <li>1 Active pickem (today at 1am)</li>
                    <li>30 Completed pickems (past dates with answers marked and scored)</li>
                  </ul>
                  <li>
                    Each pickem includes 6 questions: Team, Player, Prop, Over/Under, Player
                    (assists), and Tiebreaker
                  </li>
                  <li>~72 user selections per completed pickem (60% participation rate)</li>
                </ul>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="w-full sm:w-auto"
                >
                  {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isGenerating ? 'Generating...' : 'Generate Test Data'}
                </Button>
                {progressMessages.length > 0 && (
                  <Button variant="outline" onClick={handleClearLog} disabled={isGenerating}>
                    Clear Log
                  </Button>
                )}
              </div>

              {/* Progress Log */}
              {progressMessages.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      Progress Log
                      {generationStatus === 'generating' && (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      )}
                      {generationStatus === 'success' && (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      )}
                      {generationStatus === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                    </h3>
                  </div>
                  <div className="border rounded-lg bg-slate-950 dark:bg-slate-900">
                    <ScrollArea className="h-80">
                      <div ref={scrollRef} className="p-4 space-y-1 font-mono text-xs">
                        {progressMessages.map((msg, index) => (
                          <div key={index} className="flex gap-3">
                            <span className="text-slate-500 select-none">
                              [{formatTimestamp(msg.timestamp)}]
                            </span>
                            <span className={getMessageColor(msg.type)}>
                              {msg.message}
                              {msg.stats && (
                                <div className="mt-1 ml-2 space-y-0.5">
                                  <div>Duration: {msg.duration}s</div>
                                  <div>Total Users: {msg.stats.totalUsers}</div>
                                  <div>Pickems Created: {msg.stats.pickemsCreated}</div>
                                  <div>Questions per Pickem: {msg.stats.questionsPerPickem}</div>
                                  <div>Avg Users per Pickem: {msg.stats.avgUsersPerPickem}</div>
                                </div>
                              )}
                              {msg.error && msg.details && (
                                <div className="mt-1 ml-2 text-red-400">Details: {msg.details}</div>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Demo Pickem Card - Hide in production */}
        {!isProduction && (
          <Card className="border-green-200 dark:border-green-900">
            <CardHeader>
              <CardTitle className="text-green-600 dark:text-green-400">
                Create Demo Pickem
              </CardTitle>
              <CardDescription>
                Create a single ACTIVE, immediately-playable pickem on this client&apos;s default
                tenant that exercises every question type (team, player, prop, over/under, custom,
                tiebreaker, leader, and the Moneyline / Spread / Total main markets).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                <AlertDescription className="text-green-800 dark:text-green-200">
                  Safe to run repeatedly — it wipes and recreates its own contest by title
                  (&quot;All Question Types - Playable Test&quot;), so it never accumulates
                  duplicates and simply reopens a fresh copy.
                </AlertDescription>
              </Alert>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => createDemoPickemMutation.mutate(undefined)}
                      disabled={createDemoPickemMutation.isPending}
                      className="w-full sm:w-auto border-green-500 text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950"
                    >
                      {createDemoPickemMutation.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      {createDemoPickemMutation.isPending ? 'Creating...' : 'Create Demo Pickem'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Seeds one ACTIVE pickem on this client&apos;s default tenant with every
                      question type and the main markets, open for picks with a start time 7 days
                      out. Re-running replaces the previous demo pickem.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardContent>
          </Card>
        )}

        {/* Flush Cache Card */}
        <Card className="border-yellow-200 dark:border-yellow-900">
          <CardHeader>
            <CardTitle className="text-yellow-600 dark:text-yellow-400">Cache Management</CardTitle>
            <CardDescription>Clear all Redis cache keys</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950">
              <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                ⚠️ This will flush all Redis cache keys. Cached data will be regenerated on next
                request.
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={handleFlushCache}
                      disabled={flushCacheMutation.isPending}
                      className="border-yellow-500 text-yellow-700 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:bg-yellow-950"
                    >
                      {flushCacheMutation.isPending ? 'Flushing...' : 'Flush Redis Cache'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Flushes all Redis cache keys across every game type</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => bustDailyRevealCacheMutation.mutate({ friendlyId: 12 })}
                      disabled={bustDailyRevealCacheMutation.isPending}
                      className="border-blue-500 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                    >
                      {bustDailyRevealCacheMutation.isPending
                        ? 'Busting...'
                        : 'Bust Daily Reveal #12 Cache'}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Refills the per-game and list caches for Daily Reveal friendly_id=12 from the
                      database. Use this when the admin list shows a stale status for that contest.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardContent>
        </Card>

        {/* Redis Monitor Card */}
        <Card className="border-purple-200 dark:border-purple-900">
          <CardHeader>
            <CardTitle className="text-purple-600 dark:text-purple-400 flex items-center gap-2">
              <Database className="h-5 w-5" />
              Redis Monitor
            </CardTitle>
            <CardDescription>
              Monitor Redis connection health and browse cached data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Connection Status */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Connection Status
              </h3>
              {redisHealthQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking connection...
                </div>
              ) : redisHealthQuery.data ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Badge
                    variant={
                      redisHealthQuery.data.status === 'connected' ? 'default' : 'destructive'
                    }
                    className={
                      redisHealthQuery.data.status === 'connected'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : ''
                    }
                  >
                    {redisHealthQuery.data.status === 'connected' ? (
                      <>
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Connected
                      </>
                    ) : (
                      <>
                        <XCircle className="h-3 w-3 mr-1" />
                        Error
                      </>
                    )}
                  </Badge>
                  {redisHealthQuery.data.status === 'connected' && (
                    <>
                      <span className="text-sm text-muted-foreground">
                        Latency:{' '}
                        <span className="font-mono font-medium">
                          {redisHealthQuery.data.latencyMs}ms
                        </span>
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Keys:{' '}
                        <span className="font-mono font-medium">
                          {redisHealthQuery.data.keyCount}
                        </span>
                      </span>
                    </>
                  )}
                  {redisHealthQuery.data.status === 'error' && redisHealthQuery.data.message && (
                    <span className="text-sm text-red-600 dark:text-red-400">
                      {redisHealthQuery.data.message}
                    </span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => redisHealthQuery.refetch()}
                    disabled={redisHealthQuery.isFetching}
                  >
                    {redisHealthQuery.isFetching ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      'Refresh'
                    )}
                  </Button>
                </div>
              ) : null}
            </div>

            {/* Data Browser */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Data Browser</h3>
              <div className="flex gap-2">
                <Input
                  value={redisPattern}
                  onChange={e => setRedisPattern(e.target.value)}
                  placeholder="Key pattern (e.g. * or user:*)"
                  className="font-mono text-sm max-w-xs"
                  onKeyDown={e => e.key === 'Enter' && handleRedisSearch()}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRedisSearch}
                  disabled={redisKeysQuery.isFetching}
                >
                  {redisKeysQuery.isFetching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Search'
                  )}
                </Button>
                {redisSearchEnabled && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRedisSearchEnabled(false);
                      setExpandedKey(null);
                      setSelectedRedisKey(null);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {redisKeysQuery.data && (
                <div className="space-y-2">
                  {redisKeysQuery.data.truncated && (
                    <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950 py-2">
                      <AlertDescription className="text-yellow-800 dark:text-yellow-200 text-sm">
                        Showing first 500 of {redisKeysQuery.data.count} keys
                      </AlertDescription>
                    </Alert>
                  )}
                  {redisKeysQuery.data.keys.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No keys found matching pattern</p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="grid grid-cols-[1fr_80px_80px_20px] gap-2 px-3 py-2 bg-muted text-xs font-semibold text-muted-foreground">
                        <span>Key</span>
                        <span>Type</span>
                        <span>TTL</span>
                        <span></span>
                      </div>
                      <div className="divide-y max-h-96 overflow-y-auto">
                        {redisKeysQuery.data.keys.map((keyInfo: RedisKeyInfo) => (
                          <div key={keyInfo.key}>
                            <button
                              className="w-full grid grid-cols-[1fr_80px_80px_20px] gap-2 px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                              onClick={() => handleRedisKeyClick(keyInfo.key)}
                            >
                              <span className="font-mono text-xs truncate">{keyInfo.key}</span>
                              <span>
                                <Badge variant="outline" className="text-xs font-mono">
                                  {keyInfo.type}
                                </Badge>
                              </span>
                              <span className="text-xs text-muted-foreground font-mono">
                                {keyInfo.ttl === -1
                                  ? '∞'
                                  : keyInfo.ttl === -2
                                    ? 'expired'
                                    : `${keyInfo.ttl}s`}
                              </span>
                              <span className="flex items-center justify-center">
                                {expandedKey === keyInfo.key ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronRight className="h-3 w-3" />
                                )}
                              </span>
                            </button>
                            {expandedKey === keyInfo.key && (
                              <div className="px-3 pb-3 bg-slate-950 dark:bg-slate-900">
                                {redisValueQuery.isLoading && selectedRedisKey === keyInfo.key ? (
                                  <div className="flex items-center gap-2 text-sm text-slate-400 py-2">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Loading value...
                                  </div>
                                ) : redisValueQuery.data && selectedRedisKey === keyInfo.key ? (
                                  <pre className="text-xs text-green-400 font-mono overflow-x-auto whitespace-pre-wrap break-all py-2 max-h-48 overflow-y-auto">
                                    {formatRedisValue(redisValueQuery.data.value)}
                                  </pre>
                                ) : null}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Regenerate Aggregate Leaderboards Card */}
        <Card className="border-blue-200 dark:border-blue-900">
          <CardHeader>
            <CardTitle className="text-blue-600 dark:text-blue-400">
              Leaderboard Regeneration
            </CardTitle>
            <CardDescription>Recalculate all aggregate leaderboards from scratch</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950">
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                ⚠️ This will delete and regenerate all aggregate leaderboard data for all leagues
                and periods (today, week, month, year). Use this after making changes to the
                leaderboard calculation logic or if leaderboard data becomes inconsistent.
              </AlertDescription>
            </Alert>
            <Button
              variant="outline"
              onClick={handleRegenerateLeaderboards}
              disabled={regenerateMutation.isPending}
              className="w-full sm:w-auto border-blue-500 text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
            >
              {regenerateMutation.isPending ? 'Regenerating...' : 'Regenerate All Leaderboards'}
            </Button>
          </CardContent>
        </Card>

        {/* Resend Event Stream Submissions */}
        <Card className="border-orange-200 dark:border-orange-900">
          <CardHeader>
            <CardTitle className="text-orange-600 dark:text-orange-400">
              Resend FreeToPlay Submission Events
            </CardTitle>
            <CardDescription>
              Re-emit FreeToPlaySubmission events to the DraftKings Event Stream for a given Daily
              Reveal game. Use this to correct events that were sent with an incorrect userKey.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-orange-500 bg-orange-50 dark:bg-orange-950">
              <AlertDescription className="text-orange-800 dark:text-orange-200">
                ⚠️ This will resend submission events to the DraftKings Marketing Event Stream API.
                Events are sent in batches to avoid overloading the endpoint. Only use this when
                instructed to fix incorrect event data.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="resend-pickem-id">
                  Pickem ID <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="resend-pickem-id"
                  value={resendPickemId}
                  onChange={e => setResendPickemId(e.target.value)}
                  placeholder="UUID of the Daily Reveal game"
                  className="font-mono text-sm"
                  disabled={isResending}
                />
              </div>
              <div className="space-y-1">
                <TzDateTimeInput
                  id="resend-before-utc"
                  label="Before (optional)"
                  value={resendBeforeUtc || undefined}
                  onChange={utc => setResendBeforeUtc(utc)}
                  disabled={isResending}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="resend-batch-size">Batch Size</Label>
                <Input
                  id="resend-batch-size"
                  type="number"
                  min={1}
                  max={500}
                  value={resendBatchSize}
                  onChange={e => setResendBatchSize(e.target.value)}
                  disabled={isResending}
                  title="Number of events to send per batch (1–500)"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="resend-batch-delay">Batch Delay (ms)</Label>
                <Input
                  id="resend-batch-delay"
                  type="number"
                  min={0}
                  max={5000}
                  value={resendBatchDelayMs}
                  onChange={e => setResendBatchDelayMs(e.target.value)}
                  disabled={isResending}
                  title="Delay in milliseconds between batches (0–5000)"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handlePreviewEntries}
                disabled={!resendPickemId.trim() || previewQuery.isFetching}
                variant="outline"
                title="Run the filter query and show matching entries before resending"
              >
                {previewQuery.isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Preview Entries
              </Button>
              <Button
                onClick={handleResendEventStream}
                disabled={isResending || !resendPickemId.trim()}
                className="border-orange-500 text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-950"
                variant="outline"
                title="Resend FreeToPlaySubmission events for all matching entries"
              >
                {isResending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isResending ? 'Resending...' : 'Resend Events'}
              </Button>
              {resendMessages.length > 0 && !isResending && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setResendMessages([]);
                    setResendStatus('idle');
                  }}
                >
                  Clear Log
                </Button>
              )}
            </div>

            {/* Preview Table */}
            {previewQuery.data && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">
                    Preview —{' '}
                    <span className="text-muted-foreground font-normal">
                      {previewQuery.data.total.toLocaleString()} matching entries (latest first)
                    </span>
                  </p>
                  {previewQuery.isFetching && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                <div className="border rounded-lg overflow-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">
                          Created At ({getTimezoneAbbreviation(timezone)})
                        </th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                          Day #
                        </th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                          SSO User ID
                        </th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                          Username
                        </th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                          Status
                        </th>
                        <th className="text-left px-3 py-2 font-semibold text-muted-foreground">
                          Hits
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {previewQuery.data.entries.map(entry => (
                        <tr key={entry.entryId} className="hover:bg-muted/40">
                          <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                            {formatInUserTimezone(entry.createdAt, timezone, 'yyyy-MM-dd HH:mm:ss')}
                          </td>
                          <td className="px-3 py-1.5 text-center">{entry.dayNumber ?? '—'}</td>
                          <td className="px-3 py-1.5 font-mono">
                            {entry.ssoUserId ?? <span className="text-destructive">missing</span>}
                          </td>
                          <td className="px-3 py-1.5">{entry.username ?? '—'}</td>
                          <td className="px-3 py-1.5">{entry.status}</td>
                          <td className="px-3 py-1.5 text-center">{entry.totalHits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {previewQuery.data.totalPages > 1 && (
                  <div className="flex items-center gap-2 justify-between pt-1">
                    <span className="text-xs text-muted-foreground">
                      Page {previewQuery.data.page} of {previewQuery.data.totalPages}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={previewPage === 1 || previewQuery.isFetching}
                        onClick={() => setPreviewPage(1)}
                        title="First page"
                      >
                        «
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={previewPage === 1 || previewQuery.isFetching}
                        onClick={() => setPreviewPage(p => p - 1)}
                        title="Previous page"
                      >
                        ‹
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          previewPage === previewQuery.data.totalPages || previewQuery.isFetching
                        }
                        onClick={() => setPreviewPage(p => p + 1)}
                        title="Next page"
                      >
                        ›
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          previewPage === previewQuery.data.totalPages || previewQuery.isFetching
                        }
                        onClick={() => setPreviewPage(previewQuery.data!.totalPages)}
                        title="Last page"
                      >
                        »
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {previewQuery.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {(previewQuery.error as { message?: string })?.message ??
                    'Failed to load preview'}
                </AlertDescription>
              </Alert>
            )}

            {resendMessages.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-sm font-semibold">Progress Log</h3>
                  {resendStatus === 'running' && (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  )}
                  {resendStatus === 'success' && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                  {resendStatus === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                </div>
                <div className="border rounded-lg bg-slate-950 dark:bg-slate-900">
                  <ScrollArea className="h-64">
                    <div ref={resendScrollRef} className="p-4 space-y-1 font-mono text-xs">
                      {resendMessages.map((msg, i) => (
                        <div key={i} className="flex gap-3">
                          <span className="text-slate-500 select-none">
                            [{formatTimestamp(msg.timestamp)}]
                          </span>
                          <span className={getMessageColor(msg.type)}>
                            {msg.message}
                            {msg.error && msg.details && (
                              <div className="mt-1 ml-2 text-red-400">Details: {msg.details}</div>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete All Pickems Card - Hide in production */}
        {!isProduction && (
          <Card className="border-red-200 dark:border-red-900">
            <CardHeader>
              <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
              <CardDescription>Permanently delete all pickems and related data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  ⚠️ This will permanently delete all pickems, questions, options, and user
                  selections. This action cannot be undone.
                </AlertDescription>
              </Alert>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleteMutation.isPending}
                className="w-full sm:w-auto"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Remove All Pickems'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete all pickems, questions,
              options, user selections, and leaderboard data from the database.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete All Pickems
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
